import { buildSort, goPromise } from "@delanobgt/admin-core/util/helpers";
import {
  ApiClientFactory,
  IMeta,
  IPagination,
  ISort,
} from "@delanobgt/admin-core/util/types";
import { CancelTokenSource } from "axios";
import _ from "lodash";

import {
  ECurrencyRateSortField,
  ICurrencyRate,
  PCurrencyRate,
  PCurrencyRateFilter,
} from "../entities";
import ICurrencyRateRepository from "../repository";
import { ICurrencyUpdateRequest } from "../request/request";

export default class CurrencyRateRepository
  implements ICurrencyRateRepository<CancelTokenSource> {
  private apiClientFactory: ApiClientFactory;

  // api routes
  private CURRENCY_RATE_ROUTE = "/admin/currency-rate";

  constructor(apiClientFactory: ApiClientFactory) {
    this.apiClientFactory = apiClientFactory;
  }

  buildFilter = (filter: PCurrencyRateFilter) => {
    const start_date = filter.start_date_enabled
      ? `${filter.start_date_start},${filter.start_date_end}`
      : undefined;

    const end_date = filter.end_date_enabled
      ? `${filter.end_date_start},${filter.end_date_end}`
      : undefined;

    const clonedFilter = { ...filter };
    delete clonedFilter.start_date_enabled;
    delete clonedFilter.start_date_start;
    delete clonedFilter.start_date_end;
    delete clonedFilter.end_date_enabled;
    delete clonedFilter.end_date_start;
    delete clonedFilter.end_date_end;

    return _.pickBy(
      {
        ...clonedFilter,
        start_date,
        end_date,
      },
      Boolean
    );
  };

  private getSignalToken(signal: CancelTokenSource) {
    return _.get(signal, "token", null);
  }

  private getError(error: any) {
    return _.get(error, "response.data.errors", "Something went wrong.");
  }

  async getCurrencyRate(
    pagination: IPagination,
    filter: PCurrencyRateFilter,
    sorts: ISort<ECurrencyRateSortField>[],
    signal?: CancelTokenSource
  ): Promise<[ICurrencyRate[], IMeta]> {
    const sort = buildSort(sorts);
    const params = _.pickBy(
      {
        ...pagination,
        ...this.buildFilter(filter),
      },
      Boolean
    );
    const [err, response] = await goPromise(
      this.apiClientFactory({ cancelToken: this.getSignalToken(signal) }).get(
        `${this.CURRENCY_RATE_ROUTE}?sort=${sort}`,
        {
          params,
        }
      )
    );
    if (err) {
      throw this.getError(err);
    }
    const { data, meta } = response.data;
    return [data, meta];
  }

  async getCurrencyRateById(id: number): Promise<ICurrencyRate> {
    const [err, response] = await goPromise(
      this.apiClientFactory().get(`${this.CURRENCY_RATE_ROUTE}/${id}`)
    );
    if (err) {
      throw this.getError(err);
    }
    return response.data;
  }

  async createCurrencyRate(currencyRate: PCurrencyRate): Promise<void> {
    const [err] = await goPromise(
      this.apiClientFactory().post(this.CURRENCY_RATE_ROUTE, currencyRate)
    );
    if (err) {
      throw this.getError(err);
    }
  }

  async updateCurrencyRate(
    id: number,
    updateRequest: ICurrencyUpdateRequest
  ): Promise<void> {
    const [err] = await goPromise(
      this.apiClientFactory().patch(
        `${this.CURRENCY_RATE_ROUTE}/${id}`,
        updateRequest
      )
    );
    if (err) {
      throw this.getError(err);
    }
  }
}
